import {socketClient} from "../libs/socket";
import {useEffect, useRef, useState} from "react";
import {
    hydrateRooms,
    initFilter,
    ROOM_KEY, ROOM_MEDIAS_KEY,
    ROOM_MESSAGES_KEY,
    ROOMS_KEY
} from "../api/public-voissa/rooms";
import _ from "lodash";
import {useQueryClient} from "@tanstack/react-query";
import {
    USER_CURRENT_KEY,
    USER_FRIENDS_KEY,
    USER_FRIENDS_SEARCH_KEY, USER_UNREAD_NOTIFICATIONS_KEY,
    USER_UNREAD_ROOMS_KEY
} from "../api/public-voissa/users";
import {useNavigate, useParams} from "react-router-dom";
import createLink from "../libs/createLink";
import {useRoomMarkRead} from "./useRoom";

const useSocketClientOptionDefault = {
    onUpdate : ({error, data}) =>{}
}

const useSocketClient = (
        key = null,
        params = {},
        options = {
            onUpdate : ({error, data}) =>{}
        }
    ) => {
    const [observer, setObserver] = useState(null);
    const [isSuccess, setIsSuccess] = useState(false);
    const [data, setData] = useState({});
    const [error, setError] = useState(null);
    let currentRoom = _.get(params, 'roomId');
    options = {
        ...useSocketClientOptionDefault,
        ...options
    };

    const optionsRef = useRef(options);
    if (!_.isEqual(optionsRef.current, options)) {
        optionsRef.current = options
    }

    const paramsRef = useRef(params);
    if (!_.isEqual(paramsRef.current, params)) {
        paramsRef.current = params
    }

    useEffect(
        () => {
            const observerClient = socketClient.subscribe(key, paramsRef.current);
            setObserver(observerClient);
            setIsSuccess(true);
            return () => {
                observerClient.unsubscribe();
                setObserver(null);
                setIsSuccess(false);
            };
        },
        [key, paramsRef, currentRoom]
    );

    useEffect(
        () => {
            if (observer) {
                observer.onUpdate(async ({error, data}) => {
                    setData(data);
                    setError(error);
                    await optionsRef.current.onUpdate({error, data})
                });
            }
        },
        [observer, optionsRef]
    );
    return {
        isSuccess,
        observer,
        data,
        error
    };
}

const insertInfinityScrollData = (queryKey, queryClient, newData) => {
    const previousValue = queryClient.getQueryData(queryKey);

    if (previousValue){
        let newPagesArray = previousValue?.pages || [];
        if (_.get(newPagesArray, '[0].data')) {
            newPagesArray[0].data = newPagesArray[0].data.filter(item => item._id !== newData._id);
            newPagesArray[0].data = [newData, ...newPagesArray[0].data];
            newPagesArray[0].total = newPagesArray[0].total + 1;
        }
        queryClient.setQueryData(queryKey, oldData => ({
            pages: newPagesArray,
            pageParams: oldData.pageParams,
        }));
    }
}

const updateInfinityScrollData = (queryKey, queryClient, newData) =>{
    const previousValue = queryClient.getQueryData(queryKey);
    if(previousValue){
        let newPagesArray = previousValue?.pages.map((page) => {
                page.data = page.data.map((item) => {
                    if(item._id.toString() === newData._id.toString()){
                        item = {...item, ...newData};
                    }
                    return item;
                });
                return page;
            }
        ) ?? [];

        queryClient.setQueryData(queryKey, oldData => ({
            pages: newPagesArray,
            pageParams: oldData.pageParams,
        }));
    }
}

const deleteInfinityScrollData = (queryKey, queryClient, newData) => {
    const previousValue = queryClient.getQueryData(queryKey);
    if (previousValue){
        let newPagesArray = previousValue?.pages.map((page) => {
                page.data = page.data.filter((item) => item._id !== newData._id);
                return page;
            }
        ) ?? [];
        queryClient.setQueryData(queryKey, oldData => ({
            pages: newPagesArray,
            pageParams: oldData.pageParams,
        }));
    }
}

const useSocketRoomMessage = (
    roomId = null
) => {
    const queryClient = useQueryClient();
    const {roomId : currentRoomId} = useParams();
    const [res, setRes] = useState({});

    const {mutate : mutateMarkRead} = useRoomMarkRead({});

    let updateData = async ({error, data}) =>{
        setRes({error, data})
        if(_.get(data,'_id')){
            if(data.op === "create"){
                insertInfinityScrollData([ROOM_MESSAGES_KEY, roomId], queryClient, data);
                if(data.kind === "gallery" || data.kind === "video"){
                    insertInfinityScrollData([ROOM_MEDIAS_KEY, roomId], queryClient, data);
                }
                if(roomId === currentRoomId && data.kind !=="involve"){
                    mutateMarkRead({roomId : currentRoomId, read : true});
                }

            }
            else if(data.op === "update" || data.op === "delete"){
                updateInfinityScrollData([ROOM_MESSAGES_KEY, roomId], queryClient, data);
                if(data.isDeleted && (data.kind === "gallery" || data.kind === "video")){
                    deleteInfinityScrollData([ROOM_MEDIAS_KEY, roomId], queryClient, data);
                }
                if(!data.isDeleted && (data.kind === "gallery" || data.kind === "video")){
                    updateInfinityScrollData([ROOM_MEDIAS_KEY, roomId], queryClient, data);
                }
            }
        }
    }
    useSocketClient('ROOMS_MESSAGES', {roomId}, {onUpdate : updateData});

    return res;
}

const useSocketRooms = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const {roomId : currentRoomId} = useParams();
    const [res, setRes] = useState({});
    const [unRead, setUnread] = useState(0);

    const dataCurrentUser = queryClient.getQueryData([USER_CURRENT_KEY]);

    const currentUserId = _.get(dataCurrentUser, '_id');

    const {mutate : mutateMarkRead} = useRoomMarkRead({});


    let updateData = async ({error, data}) =>{
        setRes({error, data});

        if(_.get(data,'_id')){
            data = hydrateRooms([data], currentUserId);
            data = data[0];

            const previousValueRoomMessage = queryClient.getQueryData([ROOM_KEY, data._id]);
            if(previousValueRoomMessage){
                queryClient.setQueryData([ROOM_KEY, data._id], {...previousValueRoomMessage, ...data});
            }

            if(data.op === "create"){
                await queryClient.invalidateQueries([USER_UNREAD_ROOMS_KEY]);
                insertInfinityScrollData([ROOMS_KEY, initFilter], queryClient, data);
            }
            else if(data.op === "update"){
                if(data.isDeletedByCurrentUser){
                    deleteInfinityScrollData([ROOMS_KEY, initFilter], queryClient, data);
                }
                else{
                    const queryCache = queryClient.getQueryCache().getAll();
                    let roomsQuery = queryCache.filter(cache => cache.queryKey[0] === ROOMS_KEY);
                    if(roomsQuery){
                        let pages = _.get(roomsQuery, '[0]state.data.pages');
                        if(pages){
                            const countUnRead = pages.map((page) => ({
                                total: page.data.filter((room) => room._id === data._id ? !data.isReadByCurrentUser : !room.isReadByCurrentUser).length,
                            })).filter(item => item.total > 0).length;

                            if(unRead !== countUnRead){
                                await queryClient.invalidateQueries([USER_UNREAD_ROOMS_KEY]);
                            }
                            setUnread(countUnRead);
                        }

                    }
                    updateInfinityScrollData([ROOMS_KEY, initFilter], queryClient, data);
                }

                await queryClient.invalidateQueries([ROOMS_KEY, {...initFilter, archived : true}]);
            }
            else if(data.op === "delete"){
                deleteInfinityScrollData([ROOMS_KEY, initFilter], queryClient, data);
                await queryClient.invalidateQueries([ROOMS_KEY, {...initFilter, archived : true}]);
            }
            else if(data.op === "exclude"){
                deleteInfinityScrollData([ROOMS_KEY, initFilter], queryClient, data);
                await queryClient.invalidateQueries([ROOMS_KEY, {...initFilter, archived : true}]);
                if(data._id === currentRoomId){
                    navigate(createLink('messengerRoom', {roomId : data._id}), {replace : true, state : {action : "exclude"}});
                }
            }
            else if(data.op === "joint"){
                insertInfinityScrollData([ROOMS_KEY, initFilter], queryClient, data);
                if(data._id === currentRoomId){
                    navigate(createLink('messengerRoom', {roomId : data._id}), {replace : true, state : {action : "joint"}});
                }
            }
        }
    }

    useEffect( () =>{
        if(currentRoomId && currentUserId){
            mutateMarkRead({roomId : currentRoomId, read : true});
            setUnread(0);
        }
    }, [currentRoomId, mutateMarkRead, currentUserId])

    useSocketClient('ROOMS', {}, {onUpdate : updateData});

    return res;
}



const useSocketFriends = () => {
    const queryClient = useQueryClient();

    const [res, setRes] = useState({});

    const queryCache = queryClient.getQueryCache()
    const queryKeys = queryCache.getAll().map(cache => cache.queryKey).filter(cache => cache[0] === USER_FRIENDS_KEY || cache[0] === USER_FRIENDS_SEARCH_KEY);

    let updateData = async ({error, data}) =>{
        setRes({error, data});
        if(_.get(data,'_id')){
            queryKeys.map(async queryKey =>{
                let friendsList = queryClient.getQueryData(queryKey);
                if(friendsList){
                    let friends = friendsList.data || [];
                    friends = friends.map(user => {
                        if(user._id === data._id){
                            user = {
                                ...user,
                                lastVisit : data.lastVisit,
                                lastActivity : data.lastActivity,
                                online : data.online
                            }
                        }
                        return user;
                    });

                    queryClient.setQueryData(queryKey, (oldData) =>{
                        return {
                            ...oldData,
                            data : friends,
                        }
                    })
                }
            });
        }
    }
    useSocketClient('USER_STATUS', {}, {onUpdate : updateData});

    return res;
}

const useSocketNotifications = () => {
    const queryClient = useQueryClient();

    const [res, setRes] = useState({});

    let updateData = async ({error, data}) =>{
        setRes({error, data});
        if(_.get(data,'total')){
            queryClient.setQueryData([USER_UNREAD_NOTIFICATIONS_KEY], () =>{
                return {
                    total : data.total || 0,
                }
            })
        }

    }

    useSocketClient('UNREAD_NOTIFICATION', {}, {onUpdate : updateData});

    return res;
}

export {
    useSocketRoomMessage,
    useSocketRooms,
    useSocketFriends,
    useSocketNotifications
};